import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { previewGuardianInvite, registerGuardianFromInvite } from '../../api/guardian';
import { clearAuth } from '../../utils/auth';
import { AUTH_NOTICE_KEY } from '../../utils/sessionManager';
import Button from '../../components/common/Button';
import PageLoadingShell from '../../components/layout/PageLoadingShell';
import PhoneNumberFields from '../../components/common/PhoneNumberFields';
import { validateStoredPhone } from '../../utils/phone';
import { TextInput, SelectInput } from '../../components/common/form';
import { apiErrorMessage, safeUserMessage } from '../../utils/apiError';

const RELATIONSHIP_KEYS = ['MOTHER', 'FATHER', 'GUARDIAN', 'GRANDPARENT', 'OTHER'];

const passwordPattern = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/;

const RegisterGuardian = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const effectiveToken = useMemo(() => (token || '').trim(), [token]);

  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    username: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    gender: 'MALE',
    relationship: 'GUARDIAN',
  });

  useEffect(() => {
    clearAuth();
  }, []);

  useEffect(() => {
    if (!effectiveToken) {
      setLoadingPreview(false);
      return;
    }
    (async () => {
      try {
        const data = await previewGuardianInvite(effectiveToken);
        setPreview(data);
      } catch {
        setPreview({ valid: false, message: t('guardianPublic.invalidLink') });
      } finally {
        setLoadingPreview(false);
      }
    })();
  }, [effectiveToken, t]);

  const setField = (name, value) => setForm((p) => ({ ...p, [name]: value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!effectiveToken) return;
    if (!form.firstName.trim() || !form.lastName.trim() || !form.username.trim()) {
      alert(t('guardianPublic.nameRequired'));
      return;
    }
    const phoneCheck = validateStoredPhone(form.phoneNumber);
    if (!phoneCheck.valid) {
      alert(phoneCheck.message);
      return;
    }
    if (!passwordPattern.test(form.password)) {
      alert(t('guardianPublic.passwordInvalid'));
      return;
    }
    if (form.password !== form.confirmPassword) {
      alert(t('guardianPublic.passwordsMismatch'));
      return;
    }

    setSubmitting(true);
    try {
      const res = await registerGuardianFromInvite(effectiveToken, {
        firstName: form.firstName.trim(),
        middleName: form.middleName.trim() || undefined,
        lastName: form.lastName.trim(),
        username: form.username.trim(),
        password: form.password,
        phoneNumber: form.phoneNumber,
        gender: form.gender,
        relationship: form.relationship,
      });
      const msg = t('guardianPublic.registrationSubmitted');
      sessionStorage.setItem(AUTH_NOTICE_KEY, msg);
      navigate('/login', { replace: true });
    } catch (err) {
      alert(apiErrorMessage(err, t('guardianPublic.registrationFailed')));
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingPreview) {
    return (
      <div className="max-w-lg w-full bg-white p-8 rounded-xl shadow">
        <PageLoadingShell variant="bare" />
      </div>
    );
  }

  if (!effectiveToken || !preview?.valid || preview?.inviteType !== 'NEW_REGISTRATION') {
    return (
      <div className="max-w-lg w-full bg-white p-8 rounded-xl shadow text-center">
        <h2 className="page-hero-title mb-3">{t('guardianPublic.inviteTitle')}</h2>
        <p className="text-red-600">{safeUserMessage(preview?.message, t('guardianPublic.invalidLink'))}</p>
      </div>
    );
  }

  return (
    <div className="max-w-lg w-full bg-white p-6 sm:p-8 rounded-xl shadow">
      <h2 className="page-hero-title mb-2">{t('guardianPublic.createAccount')}</h2>
      <p className="text-sm text-gray-600 mb-6">
        {preview.academyName && <span className="font-medium">{preview.academyName}</span>}
        {preview.studentDisplayName && (
          <>{t('guardianPublic.linkingFor')}<strong>{preview.studentDisplayName}</strong>.</>
        )}
      </p>
      {preview.invitedEmail && (
        <p className="text-sm text-gray-500 mb-4">{t('guardianPublic.invitedEmail', { email: preview.invitedEmail })}</p>
      )}

      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextInput
            label={t('forms.common.firstName')}
            value={form.firstName}
            onChange={(e) => setField('firstName', e.target.value)}
            required
          />
          <TextInput
            label={t('forms.common.lastName')}
            value={form.lastName}
            onChange={(e) => setField('lastName', e.target.value)}
            required
          />
        </div>
        <TextInput
          label={t('forms.common.middleName')}
          value={form.middleName}
          onChange={(e) => setField('middleName', e.target.value)}
        />
        <TextInput
          label={t('forms.common.loginUsername')}
          value={form.username}
          onChange={(e) => setField('username', e.target.value)}
          required
          autoComplete="username"
        />
        <PhoneNumberFields value={form.phoneNumber} onChange={(v) => setField('phoneNumber', v)} required />
        <SelectInput
          label={t('forms.common.gender')}
          value={form.gender}
          onChange={(e) => setField('gender', e.target.value)}
        >
          <option value="MALE">{t('forms.common.male')}</option>
          <option value="FEMALE">{t('forms.common.female')}</option>
          <option value="OTHER">{t('forms.common.other')}</option>
        </SelectInput>
        <SelectInput
          label={t('guardianPublic.relationship')}
          value={form.relationship}
          onChange={(e) => setField('relationship', e.target.value)}
          required
        >
          {RELATIONSHIP_KEYS.map((key) => (
            <option key={key} value={key}>{t(`guardianPublic.relationships.${key}`)}</option>
          ))}
        </SelectInput>
        <TextInput
          label={t('authPages.newPassword')}
          type="password"
          value={form.password}
          onChange={(e) => setField('password', e.target.value)}
          required
          autoComplete="new-password"
          note={t('guardianPublic.passwordNote')}
        />
        <TextInput
          label={t('guardianPublic.confirmPassword')}
          type="password"
          value={form.confirmPassword}
          onChange={(e) => setField('confirmPassword', e.target.value)}
          required
          autoComplete="new-password"
        />
        <Button type="submit" variant="primary" className="w-full" disabled={submitting}>
          {submitting ? t('guardianPublic.submitting') : t('guardianPublic.submitRegistration')}
        </Button>
      </form>
    </div>
  );
};

export default RegisterGuardian;
